import React from "react";

/** Shared shell so Key Metrics row cards stay equal height (`items-stretch` + `h-full`). */
export const KEY_METRIC_CARD_SHELL_CLASS =
  "flex h-full min-h-0 flex-1 min-w-0 basis-0 flex-col items-start gap-3 rounded-lg bg-white px-6 py-5 shadow-sm";

export interface KeyMetricCardWithChartProps {
  title: string;
  value: string;
  subtext?: string;
  badge?: { label: string; className?: string; labelClassName?: string };
  /** When 'below', badge is rendered under the value instead of beside it */
  badgePosition?: "inline" | "below";
  chartLabels?: string[];
  chartValues?: number[];
  chartColor?: string;
  valueClassName?: string;
  /** Format (value, label) for chart hover tooltip (e.g. (v) => `${v}%` or (v, l) => `${l}: ₹${v}L`) */
  formatTooltipValue?: (value: number, label: string) => string;
}

/**
 * Key metric card with mini area chart. Matches Financial Position card layout
 * (OperationalCashCard / MonthlyBurnCard): border, padding, title, value, chart, labels.
 */
export function KeyMetricCardWithChart(props: KeyMetricCardWithChartProps) {
  const {
    title,
    value,
    subtext,
    badge,
    badgePosition = "inline",
    valueClassName = "text-neutral-900",
  } = props;

  return (
    <div className={KEY_METRIC_CARD_SHELL_CLASS}>
      <span className="text-caption font-caption text-neutral-400 uppercase tracking-wider">
        {title}
      </span>
      <div
        className={
          badgePosition === "below" && badge
            ? "flex flex-col items-start gap-1"
            : "flex gap-3 items-baseline"
        }
      >
        <span
          className={`font-['Inter'] text-[48px] font-[700] leading-[48px] ${valueClassName}`}
        >
          {value}
        </span>
        {badge && badgePosition === "inline" && (
          <div
            className={`flex items-center rounded-md border border-solid px-2 py-0.5 ${badge.className ?? "border-neutral-200 bg-neutral-50"}`}
          >
            <span
              className={`text-caption font-caption ${badge.labelClassName ?? ""}`}
            >
              {badge.label}
            </span>
          </div>
        )}
      </div>
      {badge && badgePosition === "below" && (
        <div
          className={`flex items-center rounded-md border border-solid px-2 py-0.5 ${badge.className ?? "border-neutral-200 bg-neutral-50"}`}
        >
          <span
            className={`text-caption font-caption ${badge.labelClassName ?? ""}`}
          >
            {badge.label}
          </span>
        </div>
      )}
      {subtext && (
        <span className="text-body font-body text-neutral-500">{subtext}</span>
      )}
      {/* Chart slot removed — keep chart* props for future re-enable without API churn */}
    </div>
  );
}
